import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  TimePicker,
  Button,
  Typography,
  Card,
  Row,
  Col,
} from "antd";
import {
  FiEdit,
  FiUser,
  FiUserCheck,
  FiDollarSign,
  FiBookOpen,
} from "react-icons/fi";
import AdminDashLayout from "../../../Components/Admin/AdminDashLayout";
import useNavHeading from "../../../Hooks/CommonHooks/useNavHeading ";
import { createVisitorThunk } from "../../../Store/Slices/Admin/visitorManagement/visitor.action";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { fetchPayrollStaff } from "../../../Store/Slices/Finance/payroll/payroll.thunk";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { Option } = Select;

const DirectVisitor = () => {
  const { t } = useTranslation("common");
  const roleOptions = [
    { value: "admin", label: t("Admin"), icon: <FiUserCheck /> },
    { value: "staff", label: t("Staff"), icon: <FiUser /> },
    { value: "all", label: t("All"), icon: <FiUser /> },
    { value: "other", label: t("Other"), icon: <FiUser /> },
  ];
  useNavHeading(t("Visitor"), t("Add Direct Visitor"));
  const [visitorType] = useState("direct");
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [staffs, setStaffs] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    dispatch(fetchPayrollStaff({})).then((action) => {
      setStaffs(action?.payload?.data || []);
    });
  }, []);
  const { loading } = useSelector((state) => state.admin.visitor || {});
  const handleFormSubmit = async (values) => {
    const finalData = {
      ...values,
      type: visitorType,
      status: "Checked-in",
      category:
        values.category === "other" ? values.otherCategory : values.category,
      purposeOfVisit:
        values.purpose === "other" ? values.otherPurpose : values.purpose,
      timeIn: values.timeIn.format("HH:mm A"),
      timeOutEstimated: values.timeOutEstimated
        ? values.timeOutEstimated.format("HH:mm A")
        : null,
      visitType: "direct",
      hostEmployee: values.hostEmployee,
    };
    delete finalData.otherPurpose;
    delete finalData.otherCategory;
    try {
      const result = await dispatch(createVisitorThunk(finalData)).unwrap();
      // Accessing the name from the result structure for the success message
      toast.success(` ${result.visitor?.visitorName || t("visitor")} ${t("Added!")}`);
      form.resetFields();
      navigate("/visitor-management");
    } catch (error) {
      navigate("/visitor-management");
      toast.error(error.message || t("Failed to Add."));
    }
  };

  const category =
    Form.useWatch("category", form) || form.getFieldValue("category");
  const purpose =
    Form.useWatch("purpose", form) || form.getFieldValue("purpose");

  return (
    <AdminDashLayout>
      <div className="flex justify-center  p-10">
        <Card className=" border  shadow-md rounded-xl">
          <Title
            level={3}
            className="text-xl font-semibold text-gray-800 mb-10  pb-2"
          >
            {t("Add Direct Visitor")}
          </Title>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleFormSubmit}
            className="w-full"
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<Text strong>{t("Visitor Name")}</Text>}
                  name="visitorName"
                  rules={[
                    { required: true, message: t("Please enter visitor's name") },
                    {
                      max: 50,
                      message: t("Visitor's name should be 50 characters or less"),
                    },
                  ]}
                >
                  <Input placeholder={t("Enter visitor's name")} size="large" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<Text strong>{t("Contact Number / Email")}</Text>}
                  name="contact"
                  rules={[
                    {
                      required: true,
                      message: t("Please enter contact number or email"),
                    },
                    { max: 40 },
                  ]}
                >
                  <Input
                    placeholder={t("Enter contact number or email")}
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={<Text strong>{t("Company")}</Text>} name="company">
                  <Input placeholder={t("Enter visitor's company")} size="large" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<Text strong>{t("Purpose of Visit")}</Text>}
                  name="purpose"
                  rules={[
                    { required: true, message: t("Please select the purpose") },
                  ]}
                >
                  <Select placeholder={t("Select Purpose")} size="large">
                    <Option value="interview">{t("Interview")}</Option>
                    <Option value="meeting">{t("Meeting")}</Option>
                    <Option value="delivery">{t("Delivery")}</Option>
                    <Option value="other">{t("Other")}</Option>
                  </Select>
                </Form.Item>
              </Col>

              {purpose === "other" && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong>{t("Other Purpose")}</Text>}
                    name="otherPurpose"
                    rules={[
                      {
                        required: true,
                        message: t("Please enter the other purpose"),
                      },
                      {
                        max: 20,
                        message: t("Purpose cannot exceed 20 characters"),
                      },
                    ]}
                  >
                    <Input placeholder={t("Type your purpose" )}size="large" />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} md={12}>
                <Form.Item
                  label={<Text strong>{t("Host Employee")}</Text>}
                  name="hostEmployee"
                  rules={[
                    {
                      required: true,
                      message: t("Please select a host employee"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder={t("Search for host employee")}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    size="large"
                  >
                    {staffs?.map((st) => (
                      <Option key={st._id} value={st?._id} className="w-auto">
                        {st.firstName} {st?.lastName} | {st?.employeeID} |{" "}
                        {st?.role}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<Text strong>{t("Category")}</Text>}
                  name="category"
                  rules={[
                    { required: true, message: t("Please select a category") },
                  ]}
                >
                  <Select placeholder={t("Select Category")} size="large">
                    {roleOptions.map((cate) => (
                      <Option value={cate.value}>{cate.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {category === "other" && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong>{t("Other Category")}</Text>}
                    name="otherCategory"
                    rules={[
                      {
                        required: true,
                        message: t("Please enter the other category"),
                      },
                      {
                        max: 20,
                        message: t("Category name cannot exceed 20 characters"),
                      },
                    ]}
                  >
                    <Input placeholder={t("Type your category")} size="large" />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} md={12}>
                <Form.Item
                  label={<Text strong>{t("Time In")}</Text>}
                  name="timeIn"
                  rules={[
                    { required: true, message: t("Please enter check-in time") },
                  ]}
                >
                  <TimePicker
                    format="HH:mm"
                    placeholder={t("Select check-in time")}
                    size="large"
                    className="w-full"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item className="mb-0 flex justify-end mt-6">
              <button
                disabled={loading}
                type="submit"
                className="!btn-gradient
                !py-2 !px-10 
                text-white font-semibold tracking-wide shadow-md transition-all duration-300"
              >
                {loading ? t("Loading...") : t("Check-in Visitor")}
              </button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </AdminDashLayout>
  );
};

export default DirectVisitor;
